import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDownUp, ChevronDown, Info, Search, Square, Users, Wallet, X } from 'lucide-react';
import {
  formatIqd,
  fromMillidinars,
  type OutstandingOrderItem,
  type OutstandingTabDto,
  type SplitOrderBillResult,
} from '@gc/shared';

import { FilterDisclosure } from '@/components/layout/FilterDisclosure';
import { PoweredOffNote } from '@/components/PoweredOffNote';
import { useAuth } from '@/features/auth/useAuth';
import { BillDetailsDrawer } from '@/features/bills/BillDetailsDrawer';
import { CustomerProfileDrawer } from '@/features/customers/CustomerProfileDrawer';
import { CloseEmptyTabsButton } from '@/features/tabs/CloseEmptyTabsDialog';
import { TabCheckoutDrawer } from '@/features/tabs/TabCheckoutDrawer';
import { api } from '@/lib/api';
import { MoveVisitorDialog } from './MoveVisitorDialog';
import { OutstandingEndDialog } from './OutstandingEndDialog';
import {
  categoriesOf,
  filterOutstandingLines,
  NO_FILTERS,
  outstandingTotals,
  sectionOutstandingLines,
  toOutstandingLines,
  visitorNamesOf,
  type OutstandingFilters,
  type OutstandingLine,
  type OutstandingSection,
} from './outstandingRows';
import {
  clearLines,
  EMPTY_SELECTION,
  groupSelectionByTab,
  linesFullySelected,
  linesPartlySelected,
  orderItemSelected,
  orderPartlySelected,
  pruneSelection,
  runningSelectedSessions,
  selectionSize,
  selectionTotalMd,
  selectLines,
  toggleOrder,
  toggleOrderItem,
  toggleSession,
  type OutstandingSelection,
  type SelectedTabGroup,
} from './selection';
import {
  DEFAULT_OUTSTANDING_SORT,
  OUTSTANDING_SORTS,
  readStoredSort,
  sortOrderItems,
  sortOutstandingSections,
  storeSort,
  type OutstandingSort,
} from './outstandingSort';
import { filterChipClass } from './filterChip';
import { useOutstanding } from './useOutstanding';
import { VisitorNamePicker } from './VisitorNamePicker';

/** `sessionStorage`, or nothing where the browser refuses it. */
function sessionStore(): Storage | undefined {
  try {
    return window.sessionStorage;
  } catch {
    return undefined;
  }
}

/**
 * Outstanding — the cashier's screen (SPEC §5.3, §5.6).
 *
 * A customer arrives at the till and says a name. The cashier finds every line
 * under it — the devices they played and the food they ordered — ticks what is
 * theirs, and settles it. The rest stays owed under the same name for whoever
 * is still playing.
 *
 * ONE GRID, SECTIONED BY PARTY, a row per unpaid thing — the owner's request,
 * twice, and the second one narrowed the first. It began grouped in COLLAPSIBLE
 * sections, and the trouble at a counter was that "what does this person owe"
 * appeared at a different height every time, so it was flattened. Flat, staff
 * then had to read the name column on every row to see where one party ended.
 *
 * So: sections, but nothing folds and nothing re-sorts. Each party gets a
 * sticky header carrying their name, their item count and their total, and the
 * rows below keep the flat grid's newest-first order. The height objection was
 * about hunting for a figure that moved; a header that never leaves the screen
 * is the part that answers it. The filter bar still does the narrowing.
 *
 * Newest first is the default and nothing re-sorts by itself; the one thing
 * that re-orders rows is the cashier choosing to, from the sort control
 * ("Highest paying" and the rest, owner 2026-09-23) — see `outstandingSort.ts`.
 *
 * Sections are cut by TAB, not by name — see `sectionOutstandingLines`. A
 * header's total has to be a figure some Pay button can actually charge.
 *
 * **Settlement is still per party**, and that is not a detail: every row keeps
 * its `tabId`, a selection spanning two parties splits into one checkout each,
 * and no arithmetic below changed. See `outstandingRows.ts`.
 *
 * This does NOT read the bills list. A reservation bill is born PAID and does
 * not exist until checkout writes it, so `GET /bills?status=OPEN` cannot see a
 * party mid-session; see the note on `GET /tabs/outstanding`.
 */
export function OutstandingPage() {
  const { t, i18n } = useTranslation();
  const { can, venue } = useAuth();
  const currencySymbol = venue?.currencySymbol ?? 'IQD';
  const money = (md: string) =>
    formatIqd(fromMillidinars(md), { currencySymbol, locale: i18n.language });

  const { data, loading, error, reload } = useOutstanding();
  const [filters, setFilters] = useState<OutstandingFilters>(NO_FILTERS);
  /** How each party's lines are ordered — remembered for the browser session. */
  const [sort, setSort] = useState<OutstandingSort>(() => readStoredSort(sessionStore()));
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selection, setSelection] = useState<OutstandingSelection>(EMPTY_SELECTION);
  /**
   * What the checkout drawer is charging — a SNAPSHOT of ids, not the live
   * selection. Item-grain payments split their bills first, and the drawer
   * must then name the bill the split produced; deriving from the live
   * selection would hand it item ids no checkout understands.
   */
  const [paying, setPaying] = useState<{
    tabId: string;
    sessionIds: string[];
    orderBillIds: string[];
  } | null>(null);
  /** Orders being split for an item-grain payment; the Pay buttons wait on it. */
  const [splitting, setSplitting] = useState(false);
  const [splitError, setSplitError] = useState<string | null>(null);
  /** Order rows expanded to show their item lines, by bill id. */
  const [expandedOrders, setExpandedOrders] = useState<ReadonlySet<string>>(new Set());
  const [endingOpen, setEndingOpen] = useState(false);
  /** The party a selection is being moved OFF, opened from the selection bar. */
  const [movingFrom, setMovingFrom] = useState<OutstandingTabDto | null>(null);
  const [openBillId, setOpenBillId] = useState<string | null>(null);
  const [openCustomerId, setOpenCustomerId] = useState<string | null>(null);

  const tabs = data?.tabs ?? [];

  // A row vanishes the moment another till settles it, and a selection holding a
  // settled row would earn a refusal for the whole party.
  useEffect(() => {
    setSelection((current) => pruneSelection(current, tabs));
  }, [tabs]);

  const lines = useMemo(() => toOutstandingLines(tabs), [tabs]);
  const visible = useMemo(() => filterOutstandingLines(lines, filters), [lines, filters]);
  const names = useMemo(() => visitorNamesOf(lines), [lines]);
  const categories = useMemo(() => categoriesOf(lines), [lines]);
  const footer = useMemo(() => outstandingTotals(visible), [visible]);
  // Cut into parties from the newest-first rows FIRST, then order: a sort never
  // merges or splits a party, it only re-orders what is already there.
  const sections = useMemo(
    () => sortOutstandingSections(sectionOutstandingLines(visible), sort),
    [visible, sort],
  );

  const groups = useMemo(() => groupSelectionByTab(selection, tabs), [selection, tabs]);
  const selectedCount = selectionSize(selection);
  const selectedTotal = selectionTotalMd(selection, tabs);
  const runningSelected = runningSelectedSessions(selection, tabs);
  const canSettle = can('sessions.manage');
  /*
   * Taking the money is a second key (2026-09-24): `POST /tabs/:id/checkout`
   * wants `bills.create` on top of `sessions.manage`, because running the
   * floor and working the drawer are two jobs. So every Pay button — and the
   * item Pay, whose split exists only to feed that checkout — waits on both,
   * while End, moves and visitor changes stay on `sessions.manage` alone. A
   * Pay offered here would open a drawer the server then refuses, after an
   * item split had already been written for nothing.
   */
  const canCreateBills = can('bills.create');
  const canTakePayment = canSettle && canCreateBills;
  /*
   * The reason names exactly the keys that are MISSING. It used to name Create
   * bills whenever that key was absent, so someone holding neither was told to
   * get Create bills — and once the owner ticked it, Pay stayed shut with no
   * reason at all, because Open and close sessions was still missing.
   */
  const payRefusal = canTakePayment
    ? undefined
    : !canSettle && !canCreateBills
      ? t('outstanding.needsSessionsAndBills')
      : !canSettle
        ? t('outstanding.needsSessionsManage')
        : t('outstanding.needsBillsCreate');

  const isPicked = (line: OutstandingLine) =>
    line.kind === 'session'
      ? selection.sessionIds.has(line.id)
      : selection.orderBillIds.has(line.id);

  const togglePicked = (line: OutstandingLine) =>
    setSelection((current) =>
      line.kind === 'session' ? toggleSession(current, line.id) : toggleOrder(current, line.id),
    );

  const toggleItemPicked = (line: OutstandingLine, item: OutstandingOrderItem) =>
    setSelection((current) =>
      toggleOrderItem(
        current,
        { billId: line.id, itemIds: (line.items ?? []).map((entry) => entry.orderItemId) },
        item.orderItemId,
      ),
    );

  const chooseSort = (next: OutstandingSort) => {
    setSort(next);
    storeSort(sessionStore(), next);
  };

  const toggleExpanded = (billId: string) =>
    setExpandedOrders((current) => {
      const next = new Set(current);
      if (!next.delete(billId)) next.add(billId);
      return next;
    });

  /**
   * Open the checkout for a party. Whole rows go straight to the drawer;
   * item-grain picks split their bills FIRST (`POST /tabs/:id/split-order-bill`,
   * one call per partial order), and the drawer then charges the bills the
   * splits produced. A split failure keeps the drawer closed and reloads, so
   * the screen shows whatever the server actually did.
   */
  const beginPay = async (
    tabId: string,
    sessionIds: string[],
    orderBillIds: string[],
    orderItemSplits: Array<{ billId: string; orderItemIds: string[] }>,
  ): Promise<void> => {
    // Re-entry while a split is in flight would retarget the drawer the first
    // press is about to open; every Pay button is disabled on `splitting` and
    // this guard covers the render in between. Without the Create bills key
    // there is nothing to start: the split would land and the checkout refuse.
    if (splitting || !canTakePayment) return;
    setSplitError(null);
    if (orderItemSplits.length === 0) {
      setPaying({ tabId, sessionIds, orderBillIds });
      return;
    }
    setSplitting(true);
    try {
      const billIds = [...orderBillIds];
      for (const split of orderItemSplits) {
        const result = await api.post<SplitOrderBillResult>(
          `/tabs/${tabId}/split-order-bill`,
          split,
        );
        billIds.push(result.billId);
      }
      setPaying({ tabId, sessionIds, orderBillIds: billIds });
    } catch {
      setSplitError(t('outstanding.splitFailed'));
    } finally {
      setSplitting(false);
      // Either way the board re-reads: on success the split rows are real now,
      // on failure the truth beats whatever the screen assumed.
      void reload();
    }
  };

  const payGroup = (group: SelectedTabGroup) =>
    void beginPay(group.tab.tabId, group.sessionIds, group.orderBillIds, group.orderItemSplits);

  /*
   * The header checkbox: every row ON SCREEN, which after the visitor filter is
   * every row of one person (owner request).
   *
   * `visible` and not `lines` — a select-all that reached past the filters
   * would tick rows the cashier never saw and put them in front of a Pay
   * button. Unticking is the same promise in reverse: it clears these rows and
   * leaves any selection made under a different filter alone.
   */
  const allVisiblePicked = linesFullySelected(selection, visible);
  const someVisiblePicked = linesPartlySelected(selection, visible);
  const toggleAllVisible = () =>
    setSelection((current) =>
      linesFullySelected(current, visible)
        ? clearLines(current, visible)
        : selectLines(current, visible),
    );

  /** One party's tick box: everything of theirs that survived the filters. */
  const toggleSection = (section: OutstandingSection) =>
    setSelection((current) =>
      linesFullySelected(current, section.lines)
        ? clearLines(current, section.lines)
        : selectLines(current, section.lines),
    );

  /**
   * "Pay all" on a party's header — settle exactly that section.
   *
   * REPLACES the selection rather than adding to it, matching `payOne`. The
   * drawer that opens charges what is ticked, so carrying another party's rows
   * in would put them on this party's receipt.
   */
  const paySection = (section: OutstandingSection) => {
    setSelection(selectLines(EMPTY_SELECTION, section.lines));
    void beginPay(
      section.tabId,
      section.lines.filter((line) => line.kind === 'session').map((line) => line.id),
      section.lines.filter((line) => line.kind === 'order').map((line) => line.id),
      [],
    );
  };

  /**
   * One row's End, through the same dialog and the same reporting as the bulk
   * one. "End" stops the clock and settles nothing — the device becomes free
   * while the money stays owed — and asks a round-priced device for its rounds,
   * as the Device List's End does (OW19): see `OutstandingEndDialog`.
   */
  const endOne = (line: OutstandingLine): void => {
    setSelection({ ...EMPTY_SELECTION, sessionIds: new Set([line.id]) });
    setEndingOpen(true);
  };

  /** One row's Pay: its party's checkout, pre-ticked with only that row. */
  const payOne = (line: OutstandingLine): void => {
    setSelection(
      line.kind === 'session'
        ? { ...EMPTY_SELECTION, sessionIds: new Set([line.id]) }
        : { ...EMPTY_SELECTION, orderBillIds: new Set([line.id]) },
    );
    void beginPay(
      line.tabId,
      line.kind === 'session' ? [line.id] : [],
      line.kind === 'order' ? [line.id] : [],
      [],
    );
  };

  /** One ITEM's Pay: split its line off the order, then that party's checkout. */
  const payOneItem = (line: OutstandingLine, item: OutstandingOrderItem): void => {
    setSelection({
      ...EMPTY_SELECTION,
      orderItemIds: new Map([[line.id, new Set([item.orderItemId])]]),
    });
    void beginPay(line.tabId, [], [], [{ billId: line.id, orderItemIds: [item.orderItemId] }]);
  };

  if (!can('bills.view')) {
    return (
      <div className="p-6">
        <h1 className="text-lg font-semibold text-content">{t('outstanding.title')}</h1>
        <p className="mt-2 text-sm text-content-secondary">{t('outstanding.noPermission')}</p>
      </div>
    );
  }

  const dash = <span className="text-content-muted">—</span>;

  return (
    <div className="flex min-h-full flex-col">
      <header className="flex flex-wrap items-center gap-2 p-4">
        <h1 className="me-2 text-lg font-semibold text-content">{t('outstanding.title')}</h1>

        <div className="relative">
          <Search
            className="pointer-events-none absolute top-2.5 h-4 w-4 text-content-muted"
            style={{ insetInlineStart: '0.625rem' }}
            aria-hidden
          />
          <input
            value={filters.search}
            onChange={(event) =>
              setFilters((current) => ({ ...current, search: event.target.value }))
            }
            placeholder={t('outstanding.searchPlaceholder')}
            aria-label={t('outstanding.searchPlaceholder')}
            className="min-h-9 w-56 rounded-control border border-subtle bg-input ps-8 pe-3 text-sm text-content outline-none focus:border-primary"
          />
        </div>

        {/* Phone: the visitor, the category and the order fold behind one
            Filters button; the search box, which finds a name as well, stays
            out. The badge counts the visitor and the category, so a short list
            says why while the fold is shut; the order is not a filter. From
            the roomy breakpoint the fold renders nothing of its own and this
            is the one row it always was. */}
        <FilterDisclosure
          storageKey="outstanding.filters"
          badge={(filters.visitorName !== null ? 1 : 0) + (filters.category ? 1 : 0)}
        >
          {/* The fastest path through this screen: a cashier is handed a NAME. */}
          <button
            type="button"
            data-open-visitor-picker
            onClick={() => setPickerOpen(true)}
            className={filterChipClass(filters.visitorName !== null)}
          >
            {filters.visitorName ?? t('outstanding.visitorPicker.label')}
            <Users className="h-4 w-4 text-content-secondary" aria-hidden />
          </button>

          <select
            value={filters.category ?? ''}
            onChange={(event) =>
              setFilters((current) => ({ ...current, category: event.target.value || null }))
            }
            aria-label={t('outstanding.allCategories')}
            className={filterChipClass()}
          >
            <option value="">{t('outstanding.allCategories')}</option>
            {categories.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>

          {/* Highest / lowest paying first (owner, 2026-09-23) — a preference,
              not a filter, so Clear filters leaves it alone. */}
          <label
            title={t('outstanding.sort.label')}
            className={filterChipClass(sort !== DEFAULT_OUTSTANDING_SORT)}
          >
            <ArrowDownUp className="h-4 w-4 shrink-0 text-content-secondary" aria-hidden />
            <select
              data-outstanding-sort
              value={sort}
              onChange={(event) => chooseSort(event.target.value as OutstandingSort)}
              aria-label={t('outstanding.sort.label')}
              className="min-h-9 bg-input text-sm text-content outline-none"
            >
              {OUTSTANDING_SORTS.map((option) => (
                <option key={option} value={option}>
                  {t(`outstanding.sort.${option}`)}
                </option>
              ))}
            </select>
          </label>
        </FilterDisclosure>

        {filters.visitorName || filters.category || filters.search ? (
          <button
            type="button"
            data-clear-filters
            onClick={() => setFilters(NO_FILTERS)}
            className="inline-flex min-h-9 items-center gap-1.5 rounded-control px-2 text-xs text-content-secondary hover:text-content"
          >
            <X className="h-3.5 w-3.5" aria-hidden />
            {t('outstanding.clearFilters')}
          </button>
        ) : null}

        {/* Owner only: the tabs that owe nothing and have nothing running but
            were left OPEN — the second 'Yaran staff' in the cafe's search
            (owner, 2026-09-23). Renders nothing for anyone else. */}
        <span className="ms-auto">
          <CloseEmptyTabsButton />
        </span>
      </header>

      {/*
        The Pay refusal, printed once where it can be read. Every shut Pay also
        carries it as a title, but a title needs a hover and a phone has none:
        a cashier on the phone layout saw greyed Pay buttons and no reason,
        which reads as a broken screen. Only while there is something to pay.
      */}
      {payRefusal && lines.length > 0 ? (
        <p
          data-pay-refusal
          className="flex items-start gap-2 border-b border-subtle px-4 py-2 text-xs text-content-muted"
        >
          <Info className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
          <span>{payRefusal}</span>
        </p>
      ) : null}

      {/*
        THE SELECTION BAR SITS AT THE TOP, under the filters and above the rows.

        It used to live in the footer beside the running totals, which read
        well on a laptop and badly at the counter: the list is long, the till
        is often scrolled halfway down someone's rows, and the buttons that
        act on what was just ticked were at the far end of the screen from the
        ticking. Here they appear immediately under the hand that selected.

        OUTSIDE the scrolling list, deliberately — not `sticky` inside it. The
        table's own header is already sticky within that scrollport, and a
        second sticky layer stacked on it is what makes one of them stop
        pinning. As a sibling above the scroll area this cannot scroll away at
        all.

        The always-on totals stay in the footer. They are the shift figure a
        cashier reads constantly, they are not about the selection, and moving
        them here would have made the top of the screen jump every time a row
        was ticked.
      */}
      {selectedCount > 0 ? (
        <div
          data-outstanding-actions
          // `sticky top-0` costs nothing where this sits above the scrollport
          // and saves the narrow layout, where the page itself is what scrolls.
          // z-30 clears the table head's z-20 without entering its stacking
          // context — the two never compete, and this must never be under one.
          className="sticky top-0 z-30 flex flex-wrap items-center gap-x-4 gap-y-2 border-y border-strong bg-raised px-4 py-2"
        >
          {/* What is actually about to be charged. Cyan with its glow: the
              selected total is the number the customer is told. */}
          <span className="inline-flex items-center gap-2 text-sm font-semibold text-content">
            <span className="tabular grid h-5 min-w-5 place-items-center rounded-full bg-primary px-1 text-[11px] font-bold text-primary-fg">
              {selectedCount}
            </span>
            <span className="tabular text-lg font-bold text-primary [text-shadow:var(--glow-primary)]">
              {money(selectedTotal.toString())}
            </span>
          </span>

          <div className="flex-1" />

          <button
            type="button"
            onClick={() => setSelection(EMPTY_SELECTION)}
            className="inline-flex items-center gap-1.5 rounded-full border border-subtle px-3 py-1.5 text-xs text-content-secondary transition-colors hover:border-danger/60 hover:bg-hover hover:text-danger"
          >
            <X className="h-3.5 w-3.5" aria-hidden />
            {t('outstanding.clear')}
          </button>

          {/*
            Move what is ticked onto somebody else's tab — a merge when it is
            everything, a split when it is some of it.

            ONE PARTY AT A TIME, and the button says so rather than half-working:
            a move has ONE source tab (`POST /tabs/:id/move-items`), so a
            selection spanning two names has no single answer to "move it off
            whom". Settling has the same shape and solves it with one Pay button
            per party; here the honest thing is to ask the cashier to narrow.
          */}
          <button
            type="button"
            data-action="change-visitor"
            disabled={!canSettle || groups.length !== 1 || selection.orderItemIds.size > 0}
            onClick={() => {
              const group = groups[0];
              if (group) setMovingFrom(group.tab);
            }}
            title={
              selection.orderItemIds.size > 0
                ? t('outstanding.moveWholeOrders')
                : groups.length > 1
                  ? t('outstanding.moveOneParty')
                  : undefined
            }
            className="inline-flex items-center justify-center gap-2 rounded-control border border-strong bg-overlay px-3 py-1.5 text-sm font-semibold text-content transition-colors hover:bg-hover disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Users className="h-3.5 w-3.5 shrink-0" aria-hidden />
            {t('outstanding.moveVisitor')}
          </button>

          <button
            type="button"
            data-action="end"
            disabled={!canSettle || runningSelected.length === 0}
            onClick={() => setEndingOpen(true)}
            title={runningSelected.length === 0 ? t('outstanding.nothingRunning') : undefined}
            className="inline-flex items-center justify-center gap-2 rounded-control border border-strong bg-overlay px-3 py-1.5 text-sm font-semibold text-content transition-colors hover:bg-hover disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Square className="h-3.5 w-3.5 shrink-0 fill-current" aria-hidden />
            {t('outstanding.end')}
            <span className="tabular rounded-full bg-white/10 px-1.5 py-0.5 text-[11px] font-bold">
              {runningSelected.length}
            </span>
          </button>

          {/* One button per party, because settlement is per party — a
              selection spanning two names cannot be one receipt. */}
          {groups.map((group) => (
            <button
              key={group.tab.tabId}
              type="button"
              data-action="pay"
              data-pay-tab={group.tab.tabId}
              disabled={!canTakePayment || splitting}
              title={payRefusal}
              onClick={() => payGroup(group)}
              // The screen's ONE glowing action (spec R3): taking the money.
              // Everything else here stays on hairlines.
              className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-4 py-1.5 text-sm font-semibold text-primary-fg shadow-[var(--glow-primary)] transition-colors hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-40 disabled:shadow-none"
            >
              <Wallet className="h-4 w-4 shrink-0" aria-hidden />
              <span className="truncate">
                {groups.length === 1
                  ? t('outstanding.endAndPay')
                  : t('outstanding.endAndPayFor', { name: group.tab.name })}
              </span>
              <span className="tabular rounded-full bg-black/20 px-1.5 py-0.5 text-[11px] font-bold">
                {money(group.subtotalMd.toString())}
              </span>
            </button>
          ))}
        </div>
      ) : null}

      {/* An item payment that could not be prepared. The board has already
          reloaded to the server's truth by the time this shows. */}
      {splitError ? (
        <p
          role="alert"
          className="border-b border-danger/40 bg-danger/10 px-4 py-2 text-sm text-danger"
        >
          {splitError}
        </p>
      ) : null}

      <div className="min-h-0 flex-1 overflow-auto px-4">
        {error ? (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        ) : loading && !data ? (
          <p className="text-sm text-content-muted">{t('common.loading')}</p>
        ) : visible.length === 0 ? (
          <p className="text-sm text-content-muted">
            {lines.length === 0 ? t('outstanding.allSettled') : t('common.noResults')}
          </p>
        ) : (
          <>
            {/* Phone: a card per party — the same sections and the same
              selection handlers as the grid, shaped for a thumb. A 14-column
              row dragged sideways cannot answer "what does this person owe". */}
            <div className="space-y-3 pb-3 md:hidden">
              {sections.map((section) => (
                <section
                  key={section.tabId}
                  data-mobile-section={section.tabId}
                  className="rounded-card border border-subtle bg-raised"
                >
                  {/* Party header sits on the raised ink so the section reads as
                    its own instrument panel on a scrolling list. */}
                  <header className="flex items-center gap-2.5 rounded-t-card border-b border-strong bg-overlay p-3">
                    <input
                      type="checkbox"
                      checked={linesFullySelected(selection, section.lines)}
                      ref={(box) => {
                        if (box) box.indeterminate = linesPartlySelected(selection, section.lines);
                      }}
                      onChange={() => toggleSection(section)}
                      aria-label={t('outstanding.selectParty', { name: section.visitorName })}
                      className="h-5 w-5 shrink-0 rounded-sm accent-[var(--brand-primary)]"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-content">
                        {section.visitorName}
                      </p>
                      <p className="text-xs text-content-muted">
                        {t('outstanding.sectionItems', { times: section.count })}
                        {section.liveCount > 0 ? (
                          <span className="text-ending">
                            {' · '}
                            {t('outstanding.sectionRunning', { times: section.liveCount })}
                          </span>
                        ) : null}
                      </p>
                    </div>
                    <span className="tabular text-sm font-bold text-primary">
                      {money(section.totalMd.toString())}
                    </span>
                    <button
                      type="button"
                      data-mobile-section-pay={section.tabId}
                      disabled={!canTakePayment || splitting}
                      title={payRefusal}
                      onClick={() => paySection(section)}
                      className="rounded-control bg-primary px-3 py-2 text-xs font-semibold text-primary-fg transition-colors hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      {t('outstanding.payAll')}
                    </button>
                  </header>

                  <ul>
                    {section.lines.map((line) => (
                      <li
                        key={line.id}
                        className={`border-t border-subtle/60 p-3 first:border-t-0 ${
                          line.live ? 'text-ending' : 'text-content'
                        }`}
                      >
                        <div className="flex items-center gap-2.5">
                          <input
                            type="checkbox"
                            checked={isPicked(line)}
                            ref={(box) => {
                              if (box) {
                                box.indeterminate =
                                  line.kind === 'order' && orderPartlySelected(selection, line.id);
                              }
                            }}
                            onChange={() => togglePicked(line)}
                            aria-label={t('outstanding.selectLine', {
                              name: line.deviceName ?? line.visitorName,
                            })}
                            className="h-5 w-5 shrink-0 rounded-sm accent-[var(--brand-primary)]"
                          />
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium">
                              {line.deviceName ?? line.billNumber ?? line.visitorName}
                              {line.kind === 'order' && line.productCount ? (
                                <span className="text-content-muted">
                                  {' · '}
                                  {t('outstanding.columns.products')}: {line.productCount}
                                </span>
                              ) : null}
                            </p>
                            <p className="tabular text-xs text-content-muted">
                              {new Date(line.startedAt).toLocaleString(i18n.language, {
                                dateStyle: 'short',
                                timeStyle: 'short',
                              })}
                              {line.duration ? ` · ${line.duration}` : ''}
                            </p>
                            <PoweredOffNote endedReason={line.endedReason} />
                          </div>
                          {line.kind === 'order' && line.items && line.items.length > 1 ? (
                            <button
                              type="button"
                              onClick={() => toggleExpanded(line.id)}
                              aria-expanded={expandedOrders.has(line.id)}
                              aria-label={t(
                                expandedOrders.has(line.id)
                                  ? 'outstanding.collapseItems'
                                  : 'outstanding.expandItems',
                              )}
                              className="shrink-0 rounded-control p-1.5 text-content-secondary hover:text-content"
                            >
                              <ChevronDown
                                className={`h-4 w-4 transition-transform ${
                                  expandedOrders.has(line.id) ? '' : '-rotate-90 rtl:rotate-90'
                                }`}
                                aria-hidden
                              />
                            </button>
                          ) : null}
                          <span className="tabular text-sm font-semibold">
                            {money(line.totalMd)}
                          </span>
                          {line.live ? (
                            <button
                              type="button"
                              data-mobile-row-end={line.id}
                              disabled={!canSettle}
                              onClick={() => endOne(line)}
                              className="rounded-control bg-ending px-3 py-2 text-xs font-semibold text-black transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
                            >
                              {t('outstanding.end')}
                            </button>
                          ) : (
                            <button
                              type="button"
                              data-mobile-row-pay={line.id}
                              disabled={!canTakePayment || splitting}
                              title={payRefusal}
                              onClick={() => payOne(line)}
                              className="rounded-control bg-primary px-3 py-2 text-xs font-semibold text-primary-fg transition-colors hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-40"
                            >
                              {t('outstanding.pay')}
                            </button>
                          )}
                        </div>

                        {/* The order's items, one thin row each — same grain as
                          the desktop grid's expansion. */}
                        {line.kind === 'order' &&
                        line.items &&
                        line.items.length > 1 &&
                        expandedOrders.has(line.id) ? (
                          <ul className="mt-2 space-y-1 border-s-2 border-subtle ps-3 ms-2">
                            {sortOrderItems(line.items, sort).map((item) => (
                              <li
                                key={item.orderItemId}
                                data-item-row={item.orderItemId}
                                className="flex items-center gap-2 text-xs text-content-secondary"
                              >
                                {line.splittable ? (
                                  <input
                                    type="checkbox"
                                    checked={orderItemSelected(
                                      selection,
                                      line.id,
                                      item.orderItemId,
                                    )}
                                    onChange={() => toggleItemPicked(line, item)}
                                    aria-label={t('outstanding.selectItem', { name: item.name })}
                                    className="h-4 w-4 shrink-0 rounded-sm accent-[var(--brand-primary)]"
                                  />
                                ) : null}
                                <span className="min-w-0 flex-1 truncate">
                                  {item.quantity}× {item.name}
                                </span>
                                <span className="tabular font-medium">{money(item.owedMd)}</span>
                                {line.splittable ? (
                                  <button
                                    type="button"
                                    data-mobile-item-pay={item.orderItemId}
                                    disabled={!canTakePayment || splitting}
                                    title={payRefusal}
                                    onClick={() => payOneItem(line, item)}
                                    className="rounded bg-primary/90 px-2.5 py-1 text-[11px] font-semibold text-primary-fg transition-colors hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-40"
                                  >
                                    {t('outstanding.pay')}
                                  </button>
                                ) : null}
                              </li>
                            ))}
                          </ul>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </div>

            {/*
            NO overflow here. The wrapper carries the border only: give it
            `overflow-x-auto` and it becomes its own scrollport, and every sticky
            row below silently stops sticking (measured in a browser: the head
            scrolled to -248px instead of pinning at 0). The ancestor at
            `overflow-auto` scrolls BOTH axes, so a 14-column grid still scrolls
            sideways — also measured.
          */}
            <div className="rounded-card border border-subtle max-md:hidden">
              <table className="w-full min-w-max text-sm">
                {/* Column headings in the card's table voice: 10px, tracked,
                  uppercase, on the raised ink. */}
                <thead className="sticky top-0 z-20 bg-overlay text-[10px] uppercase tracking-[0.14em] text-content-muted">
                  <tr>
                    <th className="w-8 px-3 py-2">
                      <input
                        type="checkbox"
                        data-select-all
                        checked={allVisiblePicked}
                        // Some-but-not-all shows a dash, so a cashier looking at
                        // a half-ticked list is never told it is empty or whole.
                        ref={(box) => {
                          if (box) box.indeterminate = someVisiblePicked;
                        }}
                        onChange={toggleAllVisible}
                        aria-label={t('outstanding.selectAll')}
                        title={t('outstanding.selectAll')}
                        className="h-4 w-4 rounded-sm accent-[var(--brand-primary)]"
                      />
                    </th>
                    <th className="px-3 py-2 text-start font-medium">
                      {t('outstanding.columns.billNumber')}
                    </th>
                    <th className="px-3 py-2 text-start font-medium">
                      {t('outstanding.columns.visitorName')}
                    </th>
                    <th className="px-3 py-2 text-start font-medium">
                      {t('outstanding.columns.deviceNumber')}
                    </th>
                    <th className="px-3 py-2 text-start font-medium">
                      {t('outstanding.columns.products')}
                    </th>
                    <th className="px-3 py-2 text-start font-medium">
                      {t('outstanding.columns.startDate')}
                    </th>
                    <th className="px-3 py-2 text-start font-medium">
                      {t('outstanding.columns.endDate')}
                    </th>
                    <th className="px-3 py-2 text-end font-medium">
                      {t('outstanding.columns.duration')}
                    </th>
                    <th className="px-3 py-2 text-end font-medium">
                      {t('outstanding.columns.hourlyPrice')}
                    </th>
                    <th className="px-3 py-2 text-end font-medium">
                      {t('outstanding.columns.rounds')}
                    </th>
                    <th className="px-3 py-2 text-end font-medium">
                      {t('outstanding.columns.roundPrice')}
                    </th>
                    <th className="px-3 py-2 text-end font-medium">
                      {t('outstanding.columns.total')}
                    </th>
                    <th className="px-3 py-2 text-center font-medium">
                      {t('outstanding.columns.details')}
                    </th>
                    <th className="px-3 py-2 text-center font-medium">
                      {t('outstanding.columns.action')}
                    </th>
                  </tr>
                </thead>
                {sections.map((section) => (
                  <tbody key={section.tabId} data-section={section.tabId}>
                    {/*
                    The party's own header. Sticky under the column headings so
                    the name and the figure stay on screen while the cashier
                    scrolls a long party — the reason sections were dropped
                    before was that "what does this person owe" moved height
                    every time, and a header that never leaves fixes exactly
                    that without taking the flat grid's predictability away.
                  */}
                    <tr
                      data-section-header={section.tabId}
                      className="sticky top-[2.125rem] z-10 border-t-2 border-strong bg-overlay"
                    >
                      <td className="px-3 py-1.5">
                        <input
                          type="checkbox"
                          data-section-select={section.tabId}
                          checked={linesFullySelected(selection, section.lines)}
                          ref={(box) => {
                            if (box)
                              box.indeterminate = linesPartlySelected(selection, section.lines);
                          }}
                          onChange={() => toggleSection(section)}
                          aria-label={t('outstanding.selectParty', { name: section.visitorName })}
                          title={t('outstanding.selectParty', { name: section.visitorName })}
                          className="h-4 w-4 rounded-sm accent-[var(--brand-primary)]"
                        />
                      </td>
                      <td colSpan={10} className="px-3 py-1.5">
                        <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-0.5">
                          {section.customerAccountId ? (
                            <button
                              type="button"
                              onClick={() => setOpenCustomerId(section.customerAccountId)}
                              className="text-sm font-semibold text-content underline decoration-dotted hover:text-primary"
                            >
                              {section.visitorName}
                            </button>
                          ) : (
                            <span className="text-sm font-semibold text-content">
                              {section.visitorName}
                            </span>
                          )}
                          <span className="text-xs text-content-muted">
                            {t('outstanding.sectionItems', { times: section.count })}
                          </span>
                          {/* Amber only when something in here is still running. */}
                          {section.liveCount > 0 ? (
                            <span className="text-xs font-medium text-ending">
                              {t('outstanding.sectionRunning', { times: section.liveCount })}
                            </span>
                          ) : null}
                        </span>
                      </td>
                      {/* The party's figure in brand cyan (card `.party .sum`) —
                        the number the whole section exists to answer. */}
                      <td className="tabular px-3 py-1.5 text-end text-sm font-bold text-primary">
                        {money(section.totalMd.toString())}
                      </td>
                      <td />
                      <td className="px-3 py-1.5 text-center">
                        <button
                          type="button"
                          data-section-pay={section.tabId}
                          disabled={!canTakePayment || splitting}
                          title={payRefusal}
                          onClick={() => paySection(section)}
                          className="rounded bg-primary px-3 py-1 text-xs font-semibold text-primary-fg transition-colors hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          {t('outstanding.payAll')}
                        </button>
                      </td>
                    </tr>

                    {section.lines.map((line) => (
                      <Fragment key={line.id}>
                        <tr
                          data-line-row={line.id}
                          data-live={line.live ? 'true' : undefined}
                          // A running row is amber the whole way across, so a cashier
                          // never mistakes a device somebody is still using for one
                          // that is finished and waiting to be paid. A TICKED row
                          // carries the card's cyan wash — what the Pay button will
                          // charge is visible as a block, not a scatter of ticks.
                          className={`border-t border-subtle/60 ${
                            line.live ? 'text-ending' : 'text-content'
                          } ${isPicked(line) ? 'bg-primary/[0.07]' : ''}`}
                        >
                          <td className="px-3 py-1.5">
                            <input
                              type="checkbox"
                              checked={isPicked(line)}
                              // A dash when only SOME of an order's items are ticked,
                              // so the cashier is never told a half-picked order is
                              // whole or empty — clicking it completes the order.
                              ref={(box) => {
                                if (box) {
                                  box.indeterminate =
                                    line.kind === 'order' &&
                                    orderPartlySelected(selection, line.id);
                                }
                              }}
                              onChange={() => togglePicked(line)}
                              aria-label={t('outstanding.selectLine', {
                                name: line.deviceName ?? line.visitorName,
                              })}
                              className="h-4 w-4 rounded-sm accent-[var(--brand-primary)]"
                            />
                          </td>
                          <td className="px-3 py-1.5">{line.billNumber ?? dash}</td>
                          <td className="px-3 py-1.5">
                            {line.customerAccountId ? (
                              <button
                                type="button"
                                onClick={() => setOpenCustomerId(line.customerAccountId)}
                                className="underline decoration-dotted hover:text-primary"
                              >
                                {line.visitorName}
                              </button>
                            ) : (
                              line.visitorName
                            )}
                          </td>
                          <td className="px-3 py-1.5">{line.deviceName ?? dash}</td>
                          <td className="px-3 py-1.5">
                            {/* An order with more than one line expands into its
                          items (owner request) — the count doubles as the
                          toggle so no column had to be added. */}
                            {line.kind === 'order' && line.items && line.items.length > 1 ? (
                              <button
                                type="button"
                                data-expand={line.id}
                                onClick={() => toggleExpanded(line.id)}
                                aria-expanded={expandedOrders.has(line.id)}
                                aria-label={t(
                                  expandedOrders.has(line.id)
                                    ? 'outstanding.collapseItems'
                                    : 'outstanding.expandItems',
                                )}
                                className="inline-flex items-center gap-1 font-medium text-content underline decoration-dotted hover:text-primary"
                              >
                                {line.productCount}
                                <ChevronDown
                                  className={`h-3.5 w-3.5 transition-transform ${
                                    expandedOrders.has(line.id) ? '' : '-rotate-90 rtl:rotate-90'
                                  }`}
                                  aria-hidden
                                />
                              </button>
                            ) : (
                              (line.productCount ?? dash)
                            )}
                          </td>
                          <td className="tabular px-3 py-1.5">
                            {new Date(line.startedAt).toLocaleString(i18n.language)}
                          </td>
                          <td className="tabular px-3 py-1.5">
                            {line.endedAt
                              ? new Date(line.endedAt).toLocaleString(i18n.language)
                              : dash}
                            <PoweredOffNote endedReason={line.endedReason} />
                          </td>
                          <td className="tabular px-3 py-1.5 text-end">{line.duration ?? dash}</td>
                          <td className="tabular px-3 py-1.5 text-end">
                            {line.hourlyPriceMd ? money(line.hourlyPriceMd) : dash}
                          </td>
                          <td className="tabular px-3 py-1.5 text-end">
                            {line.numberOfRounds ?? dash}
                          </td>
                          <td className="tabular px-3 py-1.5 text-end">
                            {line.roundPriceMd ? money(line.roundPriceMd) : dash}
                          </td>
                          <td className="tabular px-3 py-1.5 text-end font-semibold">
                            {money(line.totalMd)}
                          </td>
                          <td className="px-3 py-1.5 text-center">
                            {/* Only an order HAS a bill to open. A sitting's receipt
                          does not exist until it is settled. */}
                            {line.billNumber ? (
                              <button
                                type="button"
                                data-details={line.id}
                                onClick={() => setOpenBillId(line.id)}
                                aria-label={t('outstanding.columns.details')}
                                className="text-content-secondary hover:text-content"
                              >
                                <Info className="h-4 w-4" aria-hidden />
                              </button>
                            ) : (
                              dash
                            )}
                          </td>
                          <td className="px-3 py-1.5 text-center">
                            {line.live ? (
                              <button
                                type="button"
                                data-row-end={line.id}
                                disabled={!canSettle}
                                onClick={() => endOne(line)}
                                className="rounded bg-ending px-3 py-1 text-xs font-semibold text-black transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
                              >
                                {t('outstanding.end')}
                              </button>
                            ) : (
                              <button
                                type="button"
                                data-row-pay={line.id}
                                disabled={!canTakePayment || splitting}
                                title={payRefusal}
                                onClick={() => payOne(line)}
                                className="rounded bg-primary px-3 py-1 text-xs font-semibold text-primary-fg transition-colors hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-40"
                              >
                                {t('outstanding.pay')}
                              </button>
                            )}
                          </td>
                        </tr>

                        {/* The order's item lines, one thin row each: tickable and
                      payable alone when the server says the bill splits, plain
                      reading otherwise. The figure is `owedMd` — the exact
                      charge settling just that line produces. */}
                        {/* `length > 1`, matching the chevron: after a split leaves
                      one line behind there is no control to collapse with. */}
                        {line.kind === 'order' &&
                        line.items &&
                        line.items.length > 1 &&
                        expandedOrders.has(line.id)
                          ? sortOrderItems(line.items, sort).map((item) => (
                              <tr
                                key={item.orderItemId}
                                data-item-row={item.orderItemId}
                                className={`border-t border-subtle/40 text-xs text-content-secondary ${
                                  orderItemSelected(selection, line.id, item.orderItemId)
                                    ? 'bg-primary/[0.07]'
                                    : 'bg-overlay/40'
                                }`}
                              >
                                <td className="px-3 py-1 text-end">
                                  {line.splittable ? (
                                    <input
                                      type="checkbox"
                                      checked={orderItemSelected(
                                        selection,
                                        line.id,
                                        item.orderItemId,
                                      )}
                                      onChange={() => toggleItemPicked(line, item)}
                                      aria-label={t('outstanding.selectItem', { name: item.name })}
                                      className="h-3.5 w-3.5 rounded-sm accent-[var(--brand-primary)]"
                                    />
                                  ) : null}
                                </td>
                                <td colSpan={10} className="px-3 py-1 ps-8">
                                  {item.quantity}× {item.name}
                                </td>
                                <td className="tabular px-3 py-1 text-end font-medium">
                                  {money(item.owedMd)}
                                </td>
                                <td />
                                <td className="px-3 py-1 text-center">
                                  {line.splittable ? (
                                    <button
                                      type="button"
                                      data-item-pay={item.orderItemId}
                                      disabled={!canTakePayment || splitting}
                                      title={payRefusal}
                                      onClick={() => payOneItem(line, item)}
                                      className="rounded bg-primary/90 px-2.5 py-0.5 text-[11px] font-semibold text-primary-fg transition-colors hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-40"
                                    >
                                      {t('outstanding.pay')}
                                    </button>
                                  ) : null}
                                </td>
                              </tr>
                            ))
                          : null}
                      </Fragment>
                    ))}
                  </tbody>
                ))}
              </table>
            </div>
          </>
        )}
      </div>

      {/*
        The footer is ALWAYS the whole visible screen — how many lines and what
        they come to. A cashier reconciling a shift reads it constantly, and a
        figure that appeared only when something was ticked would keep vanishing
        at the moment it was wanted.

        Totals only. The buttons that act on a SELECTION moved to the bar under
        the filters — see the note there.
      */}
      <div
        data-outstanding-footer
        className="sticky bottom-0 z-20 border-t border-strong bg-raised px-4 py-2"
      >
        <div className="flex flex-wrap items-center gap-x-6 gap-y-1">
          <span className="text-xs text-content-muted">
            {t('outstanding.numberOfItems')}{' '}
            <span data-footer-count className="tabular text-sm font-semibold text-content">
              {footer.count}
            </span>
          </span>
          <span className="text-xs text-content-muted">
            {t('outstanding.columns.total')}{' '}
            {/* The money owed is the figure staff read across a counter —
                numerals get the stage (spec §2.3). */}
            <span data-footer-total className="tabular text-lg font-bold text-content">
              {money(footer.totalMd.toString())}
            </span>
          </span>
        </div>
      </div>

      {pickerOpen ? (
        <VisitorNamePicker
          names={names}
          selected={filters.visitorName}
          onPick={(name) => setFilters((current) => ({ ...current, visitorName: name }))}
          onClose={() => setPickerOpen(false)}
        />
      ) : null}

      {endingOpen ? (
        <OutstandingEndDialog
          sessions={runningSelected}
          onClose={() => setEndingOpen(false)}
          onFinished={() => void reload()}
        />
      ) : null}

      {paying ? (
        <TabCheckoutDrawer
          tabId={paying.tabId}
          currencySymbol={currencySymbol}
          // The SNAPSHOT taken when Pay was pressed — after an item split the
          // live selection still holds item ids, and only this snapshot knows
          // the bill the split produced.
          selection={{ sessionIds: paying.sessionIds, orderBillIds: paying.orderBillIds }}
          onClose={() => setPaying(null)}
          onSettled={() => {
            setSelection(EMPTY_SELECTION);
            void reload();
          }}
        />
      ) : null}

      {movingFrom ? (
        <MoveVisitorDialog
          tab={movingFrom}
          preselect={{
            sessionIds: [...selection.sessionIds],
            orderBillIds: [...selection.orderBillIds],
          }}
          onClose={() => setMovingFrom(null)}
          onMoved={() => {
            // The rows just moved to another name, so a selection still holding
            // them would put another party's items behind the Pay button.
            setSelection(EMPTY_SELECTION);
            void reload();
          }}
        />
      ) : null}

      {openBillId ? (
        <BillDetailsDrawer
          billId={openBillId}
          currencySymbol={currencySymbol}
          onClose={() => setOpenBillId(null)}
        />
      ) : null}

      {openCustomerId ? (
        <CustomerProfileDrawer
          customerId={openCustomerId}
          currencySymbol={currencySymbol}
          onClose={() => setOpenCustomerId(null)}
          onChanged={() => void reload()}
        />
      ) : null}
    </div>
  );
}
